import { Injectable, inject, Type, signal } from '@angular/core';
import { ResolveFn, Route, Routes } from '@angular/router';
import { Observable, map, catchError, of } from 'rxjs';
import { ServicioContenidoModyo } from '@core/services/modyo/modyo-contenido.service';
import { RenderizadorPaginaComponent } from '@core/components/renderizador-pagina/renderizador-pagina.component';
import { bienvenidaResolver } from '@core/resolvers/bienvenida.resolver';
import { terminosCondicionesResolver } from '@core/resolvers/terminos-condiciones.resolver';
import { datosBasicosResolver } from '@core/resolvers/datos-basicos.resolver';
import { datosEconomicosSeccion1Resolver } from '@core/resolvers/datos-economicos-seccion-1.resolver';
import { datosEconomicosSeccion2Resolver } from '@core/resolvers/datos-economicos-seccion-2.resolver';
import { confirmarDatosResolver } from '@core/resolvers/confirmar-datos.resolver';
import { firmaDigitalResolver } from '@core/resolvers/firma-digital.resolver';
import { preBiometriaResolver } from '@core/resolvers/pre-biometria.resolver';
import { confirmacionCuentaResolver } from '@core/resolvers/confirmacion-cuenta.resolver';
import { encuestaFacilidadResolver } from '@core/resolvers/encuesta-facilidad.resolver';
import { resolucionPaginaDinamica } from '@core/resolvers/pagina-dinamica.resolver';
import { sesionResolver } from '@core/resolvers/sesion.resolver';
import type { ModyoCollectionResponse } from '@modyo/sdk';

const LONGITUD_MINIMA_ENTRADAS = 0;

const PATRON_COMODIN_GLOBAL = /^\*\*$/;

type RazonRechazoRuta = 'vacia' | 'comodin_global' | 'duplicada';

interface ResultadoValidacionRuta {
  esValida: boolean;
  razonRechazo?: RazonRechazoRuta;
  mensajeError?: string;
}

interface RutaRechazada {
  rutaOriginal: string;
  slug: string;
  razon: RazonRechazoRuta;
  mensaje: string;
}

export interface ResultadoCargaRutas {
  rutas: Routes;
  rutasRechazadas: RutaRechazada[];
  tieneErrores: boolean;
  errorCargaModyo?: string;
}

interface PaginaModyo {
  meta: {
    slug: string;
    name: string;
  };
  fields: {
    ruta: string;
  };
}

@Injectable({ providedIn: 'root' })
export class ServicioConfiguracionRutasDinamicas {
  private readonly servicioModyo = inject(ServicioContenidoModyo);
  private rutasConfiguradas: Routes = [];
  private readonly rutasRechazadasSignal = signal<RutaRechazada[]>([]);
  private readonly errorCargaModyoSignal = signal<string | null>(null);
  private readonly cargaCompletadaSignal = signal<boolean>(false);

  private readonly mapaComponentesEspeciales = new Map<string, () => Promise<Type<unknown>>>([
    [
      'paginabienvenida',
      (): Promise<Type<unknown>> =>
        import('../../../feature/onboarding/pages/bienvenida').then((m) => m.BienvenidaComponent),
    ],
    [
      'paginaterminoscondiciones',
      (): Promise<Type<unknown>> =>
        import('../../../feature/onboarding/pages/terminos-condiciones').then((m) => m.TerminosCondicionesComponent),
    ],
    [
      'paginadatosbasicos',
      (): Promise<Type<unknown>> =>
        import('../../../feature/onboarding/pages/datos-basicos').then((m) => m.DatosBasicosComponent),
    ],
    [
      'paginadatoseconomicosseccion2',
      (): Promise<Type<unknown>> =>
        import('../../../feature/onboarding/pages/datos-economicos-seccion-2').then(
          (m) => m.DatosEconomicosSeccion2Component,
        ),
    ],
    [
      'paginadatoseconomicosseccion1',
      (): Promise<Type<unknown>> =>
        import('../../../feature/onboarding/pages/datos-economicos-seccion-1').then(
          (m) => m.DatosEconomicosSeccion1Component,
        ),
    ],
    [
      'paginaconfirmardatos',
      (): Promise<Type<unknown>> =>
        import('../../../feature/onboarding/pages/confirmar-datos').then((m) => m.ConfirmarDatosComponent),
    ],
    [
      'pagina-firma-digital',
      (): Promise<Type<unknown>> =>
        import('../../../feature/onboarding/pages/firma-digital').then((m) => m.FirmaDigitalComponent),
    ],
    [
      'pagina-pre-biometricos',
      (): Promise<Type<unknown>> =>
        import('../../../feature/onboarding/pages/pre-biometria').then((m) => m.PreBiometriaComponent),
    ],
    [
      'paginaconfirmacioncuenta',
      (): Promise<Type<unknown>> =>
        import('../../../feature/onboarding/pages/confirmacion-cuenta').then((m) => m.ConfirmacionCuentaComponent),
    ],
    [
      'paginaencuestafacilidad',
      (): Promise<Type<unknown>> =>
        import('../../../feature/onboarding/pages/encuesta-facilidad').then((m) => m.EncuestaFacilidadComponent),
    ],
  ]);

  private readonly mapaResolversRutas = new Map<string, Record<string, ResolveFn<unknown>>>([
    ['paginabienvenida', { paginaData: bienvenidaResolver, sesion: sesionResolver }],
    ['paginaterminoscondiciones', { paginaData: terminosCondicionesResolver, sesion: sesionResolver }],
    ['paginadatosbasicos', { paginaData: datosBasicosResolver, sesion: sesionResolver }],
    ['paginadatoseconomicosseccion2', { paginaData: datosEconomicosSeccion2Resolver, sesion: sesionResolver }],
    ['paginadatoseconomicosseccion1', { paginaData: datosEconomicosSeccion1Resolver, sesion: sesionResolver }],
    ['paginaconfirmardatos', { paginaData: confirmarDatosResolver, sesion: sesionResolver }],
    ['pagina-firma-digital', { paginaData: firmaDigitalResolver, sesion: sesionResolver }],
    ['pagina-pre-biometricos', { paginaData: preBiometriaResolver, sesion: sesionResolver }],
    ['paginaconfirmacioncuenta', { paginaData: confirmacionCuentaResolver, sesion: sesionResolver }],
    ['paginaencuestafacilidad', { paginaData: encuestaFacilidadResolver, sesion: sesionResolver }],
  ]);

  private readonly mapaRutasFallback = new Map<string, string>([
    ['paginabienvenida', 'bienvenida'],
    ['paginaterminoscondiciones', 'terminos-condiciones'],
    ['paginadatosbasicos', 'datos-basicos'],
    ['paginadatoseconomicosseccion2', 'datos-economicos-seccion-2'],
    ['paginadatoseconomicosseccion1', 'datos-economicos-seccion-1'],
    ['paginaconfirmardatos', 'confirmar-datos'],
    ['pagina-firma-digital', 'firma-electronica'],
    ['pagina-pre-biometricos', 'pre-biometrica'],
    ['paginaconfirmacioncuenta', 'confirmacion-cuenta'],
    ['paginaencuestafacilidad', 'encuesta-facilidad'],
  ]);

  private readonly resolverPorDefecto: Record<string, ResolveFn<unknown>> = {
    datosPagina: resolucionPaginaDinamica,
    sesion: sesionResolver,
  };

  public cargarRutasDesdeModyo(): Observable<Routes> {
    return this.servicioModyo.obtenerEntradas<PaginaModyo>('pagina').pipe(
      map((respuesta: ModyoCollectionResponse<PaginaModyo>): Routes => {
        this.limpiarEstadoAnterior();

        if (!respuesta.entries || respuesta.entries.length === LONGITUD_MINIMA_ENTRADAS) {
          this.cargaCompletadaSignal.set(true);
          return [];
        }

        const resultado = this.procesarEntradasConValidacion(respuesta.entries);
        this.rutasConfiguradas = resultado.rutas;
        this.rutasRechazadasSignal.set(resultado.rutasRechazadas);
        this.cargaCompletadaSignal.set(true);

        return resultado.rutas;
      }),
      catchError((error: unknown): Observable<Routes> => {
        const mensajeError = `Error cargando rutas desde Modyo: ${error instanceof Error ? error.message : String(error)}`;
        this.errorCargaModyoSignal.set(mensajeError);
        this.cargaCompletadaSignal.set(true);

        const rutasFallback = this.generarRutasFallback();
        if (rutasFallback.length > LONGITUD_MINIMA_ENTRADAS) {
          this.rutasConfiguradas = rutasFallback;
        }
        return of(rutasFallback);
      }),
    );
  }

  public generarRutasFallback(): Routes {
    const rutas: Routes = [];
    for (const [slug, path] of this.mapaRutasFallback) {
      const componenteEspecial = this.mapaComponentesEspeciales.get(slug);
      const resolvers = this.mapaResolversRutas.get(slug);
      if (componenteEspecial) {
        const ruta: Route = { path, loadComponent: componenteEspecial };
        if (resolvers) {
          ruta.resolve = resolvers;
        }
        rutas.push(ruta);
      }
    }
    return rutas;
  }

  public cargarRutasConResultadoCompleto(): Observable<ResultadoCargaRutas> {
    return this.servicioModyo.obtenerEntradas<PaginaModyo>('pagina').pipe(
      map((respuesta: ModyoCollectionResponse<PaginaModyo>): ResultadoCargaRutas => {
        this.limpiarEstadoAnterior();

        if (!respuesta.entries || respuesta.entries.length === LONGITUD_MINIMA_ENTRADAS) {
          this.cargaCompletadaSignal.set(true);
          return { rutas: [], rutasRechazadas: [], tieneErrores: false };
        }

        const resultado = this.procesarEntradasConValidacion(respuesta.entries);
        this.rutasConfiguradas = resultado.rutas;
        this.rutasRechazadasSignal.set(resultado.rutasRechazadas);
        this.cargaCompletadaSignal.set(true);

        return {
          rutas: resultado.rutas,
          rutasRechazadas: resultado.rutasRechazadas,
          tieneErrores: resultado.rutasRechazadas.length > LONGITUD_MINIMA_ENTRADAS,
        };
      }),
      catchError((error: unknown): Observable<ResultadoCargaRutas> => {
        const mensajeError = `Error cargando rutas desde Modyo: ${error instanceof Error ? error.message : String(error)}`;
        this.errorCargaModyoSignal.set(mensajeError);
        this.cargaCompletadaSignal.set(true);
        return of({
          rutas: [],
          rutasRechazadas: [],
          tieneErrores: true,
          errorCargaModyo: mensajeError,
        });
      }),
    );
  }

  public obtenerRutasRechazadas(): RutaRechazada[] {
    return this.rutasRechazadasSignal();
  }

  public obtenerErrorCargaModyo(): string | null {
    return this.errorCargaModyoSignal();
  }

  public estaCargaCompletada(): boolean {
    return this.cargaCompletadaSignal();
  }

  public tieneErroresCarga(): boolean {
    return this.errorCargaModyoSignal() !== null || this.rutasRechazadasSignal().length > LONGITUD_MINIMA_ENTRADAS;
  }

  public obtenerRutasConfiguradas(): Routes {
    return this.rutasConfiguradas;
  }

  public rutaEstaConfigurada(ruta: string): boolean {
    return this.rutasConfiguradas.some((r) => r.path === ruta);
  }

  public limpiarRutas(): void {
    this.rutasConfiguradas = [];
    this.limpiarEstadoAnterior();
  }

  private limpiarEstadoAnterior(): void {
    this.rutasRechazadasSignal.set([]);
    this.errorCargaModyoSignal.set(null);
    this.cargaCompletadaSignal.set(false);
  }

  private procesarEntradasConValidacion(entradas: PaginaModyo[]): {
    rutas: Routes;
    rutasRechazadas: RutaRechazada[];
  } {
    const rutas: Routes = [];
    const rutasRechazadas: RutaRechazada[] = [];
    const rutasYaProcesadas = new Set<string>();

    for (const entrada of entradas) {
      const ruta = entrada.fields.ruta;
      const slug = entrada.meta.slug;

      const resultadoValidacion = this.validarRuta(ruta, rutasYaProcesadas);

      if (!resultadoValidacion.esValida) {
        rutasRechazadas.push({
          rutaOriginal: ruta,
          slug,
          razon: resultadoValidacion.razonRechazo as RazonRechazoRuta,
          mensaje: resultadoValidacion.mensajeError as string,
        });
        continue;
      }

      rutasYaProcesadas.add(ruta);
      rutas.push(this.crearConfiguracionRutaConPath(entrada, ruta));
    }

    return { rutas, rutasRechazadas };
  }

  private validarRuta(ruta: string, rutasExistentes: Set<string>): ResultadoValidacionRuta {
    if (!ruta || ruta.trim() === '') {
      return {
        esValida: false,
        razonRechazo: 'vacia',
        mensajeError: 'La ruta está vacía o solo contiene espacios',
      };
    }

    if (PATRON_COMODIN_GLOBAL.test(ruta.trim())) {
      return {
        esValida: false,
        razonRechazo: 'comodin_global',
        mensajeError: `La ruta "${ruta}" es un comodín global (**) que secuestraría todo el enrutamiento`,
      };
    }

    if (rutasExistentes.has(ruta)) {
      return {
        esValida: false,
        razonRechazo: 'duplicada',
        mensajeError: `La ruta "${ruta}" ya fue definida por otra entrada del CMS`,
      };
    }

    return { esValida: true };
  }

  private crearConfiguracionRutaConPath(entrada: PaginaModyo, path: string): Route {
    const { slug } = entrada.meta;
    const componenteEspecial = this.mapaComponentesEspeciales.get(slug);
    const resolvers = this.mapaResolversRutas.get(slug) ?? this.resolverPorDefecto;

    const ruta: Route = { path, resolve: resolvers };

    if (componenteEspecial) {
      ruta.loadComponent = componenteEspecial;
    } else {
      ruta.component = RenderizadorPaginaComponent;
    }

    return ruta;
  }
}
